import React, { useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import { connect } from 'react-redux';
import { compose, withProps } from 'recompose';
import { mapDispatchToActions } from '@sparefoot/react-core/utils';
import { withShowHide } from '@sparefoot/react-core/decorators';

import { metaPropTypes, phonePropType } from 'config/propTypes';

import {
	withRouteHandler,
	withSearchHandler,
	withErrorHandler,
} from 'decorators';
import OGFFacilityPage from 'pages/OGFFacilityPage/OGFFacilityPage';
import facilitySelector from 'store/selectors/facility';
import { getFacilityCardPhoneNumber } from 'store/modules/facilities';
import { createBooking } from 'store/modules/bookings';
import { onCreateReview } from 'store/modules/reviews';
import { change as changeFormData } from 'redux-form';
import { PAGE_TYPES } from '@sparefoot/tracking-events';

import { H2 } from 'components/core/Typography';
import { Page, Section, Container } from 'components/core/Page';
import {
	FacilityBreadcrumbs,
	FacilityGallery,
	HowItWorks,
	TopBanner,
	StorageNearYou,
} from 'components/organisms';
import { OpenNow } from 'components/facility/OpenNow';
import FacilityReviews from 'components/organisms/FacilityPage/FacilityReviews/FacilityReviews';
import { FacilitySchema } from 'components/facility/FacilitySchema';
import { Notification } from 'components/core/Notification';
import { withPageViewTracking } from 'components/core/library/tracking-utils';
import { TrackingClient } from '@sparefoot/tracking-client';
import { FacilityDetails } from '../../components/organisms/FacilityPage/FacilityDetails/FacilityDetails';
import { scrollToElement } from '@sparefoot/react-core/utils/scrolling';
import FacilityAmenitiesRanked from 'components/facility/FacilityAmenitiesRanked/FacilityAmenitiesRanked';
import OMIIcon from 'assets/images/icons/omi-Icon.svg';
import Coupon from 'assets/images/icons/coupon.svg';
import { FacilityUnitListV2 } from '../../components/facility/FacilityUnitListV2/FacilityUnitListV2';
import questionMarkIcon from 'assets/images/icons/icon-question-mark.svg';
import './FacilityPageV2.scss';
import { FeaturesAmenitiesAccordion } from 'components/facility/FacilityAmenitiesAccordion/FacilityAmenitiesAccordion';

export const enhance = compose(
	connect(
		facilitySelector,
		{
			changeFormData,
			createBooking,
			onCreateReview,
			getFacilityCardPhoneNumber,
		},
		mapDispatchToActions,
	),
	withErrorHandler,
	withRouteHandler((props) => !get(props, 'facility.location.address')),
	withSearchHandler,
	withShowHide('description'),
	withShowHide('amenities'),
	withPageViewTracking(PAGE_TYPES.FACILITY),
	withProps(() => ({
		scrollToReviews: () => scrollToElement('#facility-page-user-reviews'),
	})),
);

function computeDiscounts(itemsObj, perRow = 3) {
	if (!itemsObj) return { discountItems: [], rows: [] };

	const discountItems = Object.entries(itemsObj)
		.filter(([, value]) => value?.hasAttribute === true)
		.map(([key, value]) => ({ key, label: value.label }));

	const rows = [];
	for (let i = 0; i < discountItems.length; i += perRow) {
		rows.push(discountItems.slice(i, i + perRow));
	}

	return { discountItems, rows };
}

export function FacilityPageV2({
	actions,
	facility,
	phone,
	reviews,
	reviewPageNum,
	reviewSource,
	baseUrl,
	storageUnits,
	vehicleUnits,
	loading,
	trackingPixel,
	meta,
	scarcityShow,
	isNewSearchPage,
	scrollToReviews,
	nearbyFacilities = [],
}) {
	useEffect(() => {
		if (!facility?.inNetwork) return;
		TrackingClient.trackFacilityViewed({
			facility_id: facility.id,
			facility_name: facility.name,
			facility_promo: facility.promo,
			facility_rank: facility.rank,
		});
	}, [facility]);

	const { noIndex } = meta;
	const { discountItems: availableDiscounts, rows: discountRows } =
		useMemo(() => {
			const items = facility?.groupedAmenities?.discounts?.items || null;
			return computeDiscounts(items, 3);
		}, [facility?.groupedAmenities?.discounts?.items]);

	if (!facility.inNetwork) {
		return <OGFFacilityPage {...arguments[0]} />;
	}

	return (
		<Page
			className="facility-page-v2"
			trackingPixel={trackingPixel}
			pageType={PAGE_TYPES.FACILITY}
			title={meta.title}
			description={meta.description}
			canonical={meta.canonical}
			noIndex={noIndex}
			noFollow={noIndex}
		>
			<FacilitySchema
				facility={facility}
				phone={phone}
			/>
			{facility.isOpen && <OpenNow />}
			{!facility.isVisible && (
				<Notification permanent>
					This facility has no units available for online booking.
				</Notification>
			)}
			<Section>
				<Container className="facility-header">
					<FacilityBreadcrumbs
						facility={facility}
						baseUrl={baseUrl}
					/>
				</Container>
				<Container>
					{scarcityShow && (
						<TopBanner
							title="Reserve Now! Pricing and availability may change."
							className="top-banner__promo"
						/>
					)}
				</Container>
				<FacilityDetails
					facility={facility}
					reviews={reviews}
					numberOfRatings={facility.reviews.number}
					phone={phone}
					scrollToReviews={scrollToReviews}
				/>
			</Section>
			<FacilityGallery
				facility={facility}
				facilities={nearbyFacilities}
			/>
			<Container>
				<Section className="facility-amenities-ranked">
					<Section className="section-heading">
						<H2
							size="large"
							weight="extraBold"
							last
						>
							Key Features
						</H2>
					</Section>
					<FacilityAmenitiesRanked
						groupedAmenities={facility.groupedAmenities}
						storageUnits={storageUnits}
						vehicleUnits={vehicleUnits}
					/>
				</Section>
			</Container>
			<Section className="facility-units-section">
				<Container>
					<Section>
						{(availableDiscounts?.length > 0 ||
							facility?.hasOnlineMoveins) && (
							<section className="discount-banners">
								{/* OMI */}
								{facility?.hasOnlineMoveins && (
									<section className="content-container">
										<div className="discount-row discount-row--1">
											<TopBanner
												className="top-banner_omi"
												iconAlt="Online Move In Icon"
												icon={OMIIcon}
												title="Online Move-In Available"
												content="Rent online, skip the counter, and go straight to your space"
											/>
										</div>
									</section>
								)}

								{/* Discounts */}
								{availableDiscounts.length > 0 && (
									<section className="content-container">
										{discountRows.map((row, rowIndex) => (
											<div
												className={`discount-row discount-row--${row.length}`}
												key={rowIndex}
											>
												{row.map(({ key, label }) => (
													<div
														className="discount-item"
														key={key}
													>
														<TopBanner
															className="top-banner_discount"
															title={label
																.split('(')[0]
																.trim()}
															icon={Coupon}
															iconAlt="Discount Icon"
															tooltipIcon={
																questionMarkIcon
															}
															tooltipMessage={
																label
															}
														/>
													</div>
												))}
											</div>
										))}
									</section>
								)}
							</section>
						)}
					</Section>
					<FacilityUnitListV2
						facility={facility}
						storageUnits={[
							...storageUnits,
							...(vehicleUnits || []),
						]}
						vehicleUnits={vehicleUnits}
						changeFormData={actions.changeFormData}
						onCreateBooking={actions.createBooking}
						isNewSearchPage={isNewSearchPage}
						loading={loading}
					/>
				</Container>
			</Section>
			<Section>
				<Container>
					<FeaturesAmenitiesAccordion
						facility={facility}
						title="Facility Details"
					/>
				</Container>
			</Section>
			<Section
				id="facility-page-user-reviews"
				className="facility-units-section"
			>
				<Container>
					<H2
						size="large"
						weight="extraBold"
						className="reviews-heading"
					>
						Verified Customer Reviews
					</H2>
					<FacilityReviews
						reviews={reviews}
						facility={facility}
						reviewPageNum={reviewPageNum}
						baseUrl={baseUrl}
						source={reviewSource}
						onCreateReview={actions.onCreateReview}
					/>
				</Container>
			</Section>
			<HowItWorks />
			<Section className="facility-nearby-facilities">
				<StorageNearYou type="Facility" />
			</Section>
		</Page>
	);
}

FacilityPageV2.propTypes = {
	actions: PropTypes.shape({
		changeFormData: PropTypes.func.isRequired,
		getFacilityCardPhoneNumber: PropTypes.func.isRequired,
		createBooking: PropTypes.func.isRequired,
		onCreateReview: PropTypes.func.isRequired,
	}).isRequired,
	amenities: PropTypes.object.isRequired,
	amenitiesShowing: PropTypes.bool,
	baseUrl: PropTypes.string,
	descriptionShowing: PropTypes.bool.isRequired,
	facility: PropTypes.object.isRequired,
	nearbyFacilities: PropTypes.arrayOf(PropTypes.object),
	loading: PropTypes.bool,
	meta: PropTypes.shape(metaPropTypes),
	onToggleDescription: PropTypes.func.isRequired,
	onToggleAmenities: PropTypes.func.isRequired,
	phone: phonePropType,
	reviews: PropTypes.arrayOf(PropTypes.object),
	reviewPageNum: PropTypes.number,
	reviewSource: PropTypes.string,
	storageUnits: PropTypes.arrayOf(PropTypes.object).isRequired,
	vehicleUnits: PropTypes.arrayOf(PropTypes.object).isRequired,
	trackingPixel: PropTypes.string.isRequired,
	scarcityShow: PropTypes.bool,
	isNewSearchPage: PropTypes.bool,
	scrollToReviews: PropTypes.func,
};

export default enhance(FacilityPageV2);
